import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Save, Upload, X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import SellOutOrderSelector from '../../components/sellout/SellOutOrderSelector';
import SellOutPromiseLineEditor, { makeSellOutPromiseLine } from '../../components/sellout/SellOutPromiseLineEditor';
import SellOutPromiseSummary from '../../components/sellout/SellOutPromiseSummary';
import { numberValue, supplierLabel, todayIso } from '../../components/sellout/selloutUtils';
import API, { SellOutAPI } from '../../services/api';
import { addTab } from '../../tabs/tabsSlice';

const steps = ['Datos generales', 'Ordenes base', 'Lineas de control', 'Resumen'];

const emptyForm = () => ({
  supplier_id: null,
  date: todayIso(),
  reference: '',
  comment: '',
  evidence: null,
});

export default function SellOutPromiseCreate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(emptyForm());
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [lines, setLines] = useState([makeSellOutPromiseLine()]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ general: [], lines: {} });
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });

  const showSnack = useCallback((severity, message) => {
    setSnack({ open: true, severity, message });
  }, []);

  useEffect(() => {
    dispatch(addTab({ label: 'Nueva promesa Sell Out', path: '/sellout/promotions/create' }));
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    setLoadingSuppliers(true);
    API.get('suppliers/minimal/', { params: { active: true } })
      .then(({ data }) => {
        if (mounted) setSuppliers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mounted) showSnack('error', 'No se pudo cargar proveedores.');
      })
      .finally(() => {
        if (mounted) setLoadingSuppliers(false);
      });
    return () => {
      mounted = false;
    };
  }, [showSnack]);

  const supplier = useMemo(
    () => suppliers.find((item) => item.id === form.supplier_id) || null,
    [form.supplier_id, suppliers]
  );

  const selectedOrders = useMemo(
    () => orders.filter((order) => selectedOrderIds.includes(order.id)),
    [orders, selectedOrderIds]
  );

  useEffect(() => {
    const allowedOrderIds = new Set(selectedOrderIds);
    setLines((prev) => prev.map((line) => ({
      ...line,
      base_products_payload: (line.base_products_payload || []).filter((row) => allowedOrderIds.has(row.order_id)),
    })));
  }, [selectedOrderIds]);

  const loadOrders = useCallback(async () => {
    if (!form.supplier_id) {
      setOrders([]);
      setSelectedOrderIds([]);
      return;
    }
    setLoadingOrders(true);
    try {
      const { data } = await API.get('order-Av/get_orders_with_availability/', {
        params: { supplier_id: form.supplier_id },
      });
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
      showSnack('error', 'No se pudieron cargar las ordenes del proveedor.');
    } finally {
      setLoadingOrders(false);
    }
  }, [form.supplier_id, showSnack]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const setFormValue = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateStep = (step) => {
    const general = [];
    const lineErrors = {};

    if (step >= 0) {
      if (!form.supplier_id) general.push('Seleccione proveedor.');
      if (!form.date) general.push('Seleccione fecha.');
    }
    if (step >= 1 && selectedOrderIds.length === 0) {
      general.push('Seleccione al menos una orden base.');
    }
    if (step >= 2) {
      if (!lines.length) general.push('Agregue al menos una linea.');
      lines.forEach((line, index) => {
        const messages = [];
        if (!line.base_products_payload?.length) messages.push(`Linea ${index + 1}: seleccione productos base.`);
        if (numberValue(line.expected_qty) <= 0) messages.push(`Linea ${index + 1}: cantidad esperada debe ser mayor a 0.`);
        if (line.expected_variant_mode !== 'pending' && !line.expected_product_id) {
          messages.push(`Linea ${index + 1}: seleccione producto esperado.`);
        }
        if (messages.length) lineErrors[line.client_id] = messages;
      });
    }

    setErrors({ general, lines: lineErrors });
    return general.length === 0 && Object.keys(lineErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const buildPayload = () => ({
    supplier_id: form.supplier_id,
    date: form.date,
    reference: form.reference || null,
    comment: form.comment || null,
    orders_payload: selectedOrderIds.map((orderId) => ({ order_id: orderId })),
    lines_payload: lines.map((line) => ({
      expected_product_id: line.expected_product_id || null,
      expected_format_id: line.expected_format_id || null,
      expected_variant_mode: line.expected_variant_mode,
      expected_qty: numberValue(line.expected_qty),
      comment: line.comment || null,
      base_products_payload: (line.base_products_payload || []).map((row) => ({
        order_id: row.order_id,
        order_detail_id: row.order_detail_id,
        product_id: row.product_id,
        quantity_base: numberValue(row.quantity_base),
      })),
    })),
  });

  const handleSave = async () => {
    if (!validateStep(2)) return;
    setSaving(true);
    try {
      const { data } = await SellOutAPI.commitments.create(buildPayload());
      if (form.evidence && data?.id) {
        const fd = new FormData();
        fd.append('evidence', form.evidence);
        await API.patch(`sellout/commitments/${data.id}/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      showSnack('success', 'Promesa Sell Out creada.');
      navigate('/sellout/promotions', {
        replace: true,
        state: { message: 'Promesa Sell Out creada.' },
      });
    } catch (error) {
      const detail = error?.response?.data;
      const message = typeof detail === 'string'
        ? detail
        : detail?.detail || 'No se pudo guardar la promesa Sell Out.';
      showSnack('error', message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', width: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Nueva promesa Sell Out</Typography>
          <Typography variant="body2" color="text.secondary">
            {supplier ? supplierLabel(supplier) : 'Proveedor pendiente'}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ArrowLeft size={17} />} onClick={() => navigate('/sellout/promotions')}>
          Volver
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 1 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {errors.general.length > 0 ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general.join(' ')}
          </Alert>
        ) : null}

        {activeStep === 0 ? (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Autocomplete
                fullWidth
                options={suppliers}
                value={supplier}
                loading={loadingSuppliers}
                onChange={(_, value) => {
                  setForm(emptyForm());
                  setFormValue('supplier_id', value?.id || null);
                  setSelectedOrderIds([]);
                  setLines([makeSellOutPromiseLine()]);
                }}
                getOptionLabel={supplierLabel}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => <TextField {...params} size="small" label="Proveedor" />}
              />
              <TextField
                size="small"
                type="date"
                label="Fecha"
                value={form.date}
                onChange={(event) => setFormValue('date', event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { md: 190 } }}
              />
              <TextField
                size="small"
                label="Referencia"
                value={form.reference}
                onChange={(event) => setFormValue('reference', event.target.value)}
                sx={{ minWidth: { md: 220 } }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Button component="label" variant="outlined" startIcon={<Upload size={17} />} sx={{ minWidth: 180 }}>
                Evidencia
                <input
                  hidden
                  type="file"
                  onChange={(event) => setFormValue('evidence', event.target.files?.[0] || null)}
                />
              </Button>
              {form.evidence ? (
                <Button variant="text" color="inherit" startIcon={<X size={16} />} onClick={() => setFormValue('evidence', null)}>
                  {form.evidence.name}
                </Button>
              ) : null}
            </Stack>

            <TextField
              size="small"
              multiline
              minRows={3}
              label="Comentario"
              value={form.comment}
              onChange={(event) => setFormValue('comment', event.target.value)}
            />
          </Stack>
        ) : null}

        {activeStep === 1 ? (
          <SellOutOrderSelector
            supplier={supplier}
            orders={orders}
            selectedOrderIds={selectedOrderIds}
            onSelectedOrderIdsChange={(ids) => setSelectedOrderIds(ids.map((id) => Number(id)))}
            loading={loadingOrders}
            onRefresh={loadOrders}
          />
        ) : null}

        {activeStep === 2 ? (
          <SellOutPromiseLineEditor
            lines={lines}
            onChange={setLines}
            selectedOrders={selectedOrders}
            errors={errors.lines}
          />
        ) : null}

        {activeStep === 3 ? (
          <SellOutPromiseSummary
            supplier={supplier}
            form={form}
            selectedOrders={selectedOrders}
            lines={lines}
          />
        ) : null}

        <Stack direction="row" justifyContent="space-between" spacing={1.5} sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0 || saving}>
            Anterior
          </Button>
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext} disabled={saving}>
              Siguiente
            </Button>
          ) : (
            <Button variant="contained" startIcon={<Save size={17} />} onClick={handleSave} disabled={saving}>
              Guardar promesa
            </Button>
          )}
        </Stack>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
